import {
  AppLaunchButtonClickPayload,
  OrdersRealEstate,
  ReferralRealEstate,
} from '@getvim/internal-vim-os-sdk/types';
import { ReferralStatus } from '@getvim-os/types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AppDispatch } from '../../reducers/appReducer';
import { Diagnosis, OrderType } from '@getvim-os/types';
import { ReactVimApi, useApi, useResources } from '@getvim/internal-vim-os-sdk/react';
import { Order, Patient } from '../../../../types';
import { useOrderAssistLegacyState } from '../../../../../../vim-os-sdk-migration/ehr-state';
import { orderAssistLogger } from '../../../../logger';
import { SelectButtonCallbacks, UseSelectProviderButtonProps } from './types';
import { useOrderAssistAppFeatureFlags } from '../../OrderAssistFFWrapper';

const useSelectProviderWidgetsData = ({
  patient,
  appDispatch,
}: {
  patient: Patient | undefined;
  appDispatch: AppDispatch;
}) => {
  const [diagnosis, setDiagnosis] = useState<Diagnosis[]>();
  const [selectedOrders, setSelectedOrders] = useState<Order[]>();
  const { patient: patientInContext, referral, orders } = useOrderAssistLegacyState();

  useEffect(() => {
    if (referral) {
      const { diagnosis: newDiagnosis } = referral || {};
      setDiagnosis(newDiagnosis);
    } else {
      setDiagnosis(undefined);
    }
  }, [referral]);

  useEffect(() => {
    if (orders?.selectedOrders?.length) {
      if (patientInContext?.patientId && patient?.patientId === patientInContext.patientId) {
        setSelectedOrders(orders.selectedOrders || []);
      } else {
        setSelectedOrders(undefined);
      }
    }
  }, [orders, patientInContext, patient, appDispatch]);

  return { diagnosis, selectedOrders };
};

export const useSdkSelectProviderButton = (
  {
    patient,
    appDispatch,
    appEnabled,
    supportOrdersButton,
    supportOrdersButtonInSignedReferralOnly,
  }: Pick<
    UseSelectProviderButtonProps,
    | 'patient'
    | 'appDispatch'
    | 'appEnabled'
    | 'supportOrdersButton'
    | 'supportOrdersButtonInSignedReferralOnly'
  >,
  {
    onDiOrdersSelectButtonClicked,
    onLabOrdersSelectButtonClicked,
    onReferralSelectButtonClicked,
  }: SelectButtonCallbacks,
): void => {
  const {
    ehrState: { referral, orders },
  } = useResources();
  const { launchButton } = useApi();
  const { selectedOrders } = useSelectProviderWidgetsData({
    appDispatch,
    patient,
  });
  const { shouldInjectReferralButtonByStatus } = useOrderAssistAppFeatureFlags();

  const launchButtonsConfig: Parameters<ReactVimApi['launchButton']['enableLaunchButtons']>[0] =
    useMemo(() => {
      let injectReferralButton = appEnabled && !!referral;
      const injectOrdersButton = appEnabled && supportOrdersButton;

      if (shouldInjectReferralButtonByStatus && supportOrdersButtonInSignedReferralOnly) {
        injectReferralButton =
          appEnabled && referral?.basicInformation?.status === ReferralStatus.SIGNED;
      }

      return {
        referral: {
          REFERRAL_SELECT_PROVIDER: injectReferralButton,
        },
        orders: {
          DI_SELECT_PROVIDER:
            injectOrdersButton &&
            !!orders?.some(({ basicInformation }) => basicInformation?.type === OrderType.DI),
          LABS_SELECT_PROVIDER:
            injectOrdersButton &&
            !!orders?.some(({ basicInformation }) => basicInformation?.type === OrderType.LAB),
        },
      };
    }, [
      orders,
      referral,
      supportOrdersButton,
      supportOrdersButtonInSignedReferralOnly,
      shouldInjectReferralButtonByStatus,
    ]);

  useEffect(() => {
    launchButton.enableLaunchButtons(launchButtonsConfig);
  }, [launchButton, launchButtonsConfig]);

  const onLaunchButtonClicked = useCallback(
    (payload?: AppLaunchButtonClickPayload) => {
      const { realEstate } = payload ?? {};

      switch (realEstate) {
        case ReferralRealEstate.REFERRAL_SELECT_PROVIDER:
          onReferralSelectButtonClicked();
          break;

        case OrdersRealEstate.DI_SELECT_PROVIDER:
          onDiOrdersSelectButtonClicked(selectedOrders);
          break;

        case OrdersRealEstate.LABS_SELECT_PROVIDER:
          onLabOrdersSelectButtonClicked(selectedOrders);
          break;

        default:
          orderAssistLogger.warning('Unexpected launch button trigger', {
            payload,
          });
          break;
      }
    },
    [
      onDiOrdersSelectButtonClicked,
      onLabOrdersSelectButtonClicked,
      onReferralSelectButtonClicked,
      selectedOrders,
    ],
  );

  useEffect(() => {
    launchButton.subscribe('launchButtonClicked', onLaunchButtonClicked);

    return () => {
      launchButton.unsubscribe('launchButtonClicked', onLaunchButtonClicked);
    };
  }, [launchButton, onLaunchButtonClicked]);
};
